import { cn } from '@sohanemon/utils';
import * as m from 'motion/react-m';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import type React from 'react';
import { useId, useMemo, useState } from 'react';
import { MotionProvider } from './motion';

export interface ContributionData {
	count: number;
	date: string;
	level: number;
}

export interface ContributionSummaryValues {
	days: number;
	from: string;
	to: string;
	total: number;
}

export interface ContributionGraphLabels {
	caption: string;
	contribution: (count: number) => string;
	empty: string;
	kicker: string;
	legendLess: string;
	legendMore: string;
	summary: (values: ContributionSummaryValues) => string;
}

export interface ContributionGraphProps {
	className?: string;
	data?: ContributionData[];
	/** Locale for month, weekday and tooltip date formatting. Defaults to the runtime locale. */
	locale?: string;
	labels?: Partial<ContributionGraphLabels>;
	showLegend?: boolean;
	showTooltips?: boolean;
}

interface MonthHeader {
	colspan: number;
	month: string;
	startWeek: number;
}

interface ContributionWeek {
	days: Array<ContributionData | null>;
	month: string;
	start: string;
}

interface TooltipState {
	day: ContributionData;
	x: number;
	y: number;
}

const DAYS_IN_WEEK = 7;
const FALLBACK_WINDOW_DAYS = 364;
const MIN_WEEKS_FOR_MONTH_HEADER = 2;
const NO_CONTRIBUTIONS = 0;
const NO_LEVEL = 0;
const TOOLTIP_GAP_PX = 8;
const CELL_LEVEL_CLASSES = [
	'bg-muted',
	'bg-primary/25',
	'bg-primary/50',
	'bg-primary/75',
	'bg-primary',
] as const;
const LAST_CELL_LEVEL = CELL_LEVEL_CLASSES.length - 1;
const SHOWN_WEEKDAYS = [0, 2, 4, 6];
// NOTE: 2024-01-07 is a Sunday, so each row's weekday label comes from a fixed
// reference week rather than the hovered data.
const REFERENCE_SUNDAY = new Date(2024, 0, 7);
const DEFAULT_LABELS: ContributionGraphLabels = {
	caption: 'Contribution graph for the last year',
	contribution: (count) => {
		if (count === NO_CONTRIBUTIONS) return 'No contributions';
		if (count === 1) return '1 contribution';
		return `${count} contributions`;
	},
	empty: 'No contributions',
	kicker: 'github/contributions',
	legendLess: 'Less',
	legendMore: 'More',
	summary: ({ days, total, from, to }) =>
		`${days} days tracked, ${total} contributions between ${from} and ${to}.`,
};
const EMPTY_CONTRIBUTIONS: ContributionData[] = [];

function toIsoDate(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
}

// NOTE: `YYYY-MM-DD` parsed by `new Date()` is UTC midnight, which shifts the
// weekday for anyone west of Greenwich. Build the date in local time instead.
function parseIsoDate(value: string): Date {
	const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
	return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
}

function getCellLevelClass(level: number): string {
	const index = Math.min(Math.max(level, NO_LEVEL), LAST_CELL_LEVEL);
	return CELL_LEVEL_CLASSES[index] ?? CELL_LEVEL_CLASSES[NO_LEVEL];
}

function getWindowRange(data: ContributionData[]): { end: Date; start: Date } {
	const first = data.at(0);
	const last = data.at(-1);
	const today = new Date();
	return {
		end: last ? parseIsoDate(last.date) : today,
		start: first
			? parseIsoDate(first.date)
			: addDays(today, -FALLBACK_WINDOW_DAYS),
	};
}

function buildWeeks(
	data: ContributionData[],
	formatMonth: (date: Date) => string,
): ContributionWeek[] {
	const byDate = new Map(data.map((day) => [day.date, day]));
	const { start, end } = getWindowRange(data);
	const gridStart = addDays(start, -start.getDay());

	const weeks: ContributionWeek[] = [];
	for (
		let cursor = gridStart;
		cursor <= end;
		cursor = addDays(cursor, DAYS_IN_WEEK)
	) {
		const days = Array.from({ length: DAYS_IN_WEEK }, (_, offset) => {
			const date = addDays(cursor, offset);
			if (date < start || date > end) return null;
			const iso = toIsoDate(date);
			return (
				byDate.get(iso) ?? {
					count: NO_CONTRIBUTIONS,
					date: iso,
					level: NO_LEVEL,
				}
			);
		});

		const anchor = days.find((day) => day !== null);
		if (anchor === undefined) continue;
		weeks.push({
			days,
			month: formatMonth(parseIsoDate(anchor.date)),
			start: anchor.date,
		});
	}

	return weeks;
}

function buildMonthHeaders(weeks: ContributionWeek[]): MonthHeader[] {
	const headers: MonthHeader[] = [];

	for (const [index, week] of weeks.entries()) {
		const previous = weeks[index - 1];
		if (previous?.month === week.month) {
			const current = headers.at(-1);
			if (current) current.colspan += 1;
			continue;
		}
		headers.push({ colspan: 1, month: week.month, startWeek: index });
	}

	// NOTE: a run of one column at the trailing edge is the current month, not a
	// new one — GitHub hides it, so we do too.
	const trailing = headers.at(-1);
	if (trailing && trailing.colspan < MIN_WEEKS_FOR_MONTH_HEADER) headers.pop();

	return headers;
}

// NOTE: Bun's ICU resolves the bare "ar" locale to Latin digits, so
// Arabic-Indic output must be forced to match `formatNumber` in the app.
function numberingSystem(
	locale: string | undefined,
): Intl.DateTimeFormatOptions['numberingSystem'] {
	return locale?.startsWith('ar') ? 'arab' : undefined;
}

function createDateFormatter(
	locale: string | undefined,
	options: Intl.DateTimeFormatOptions,
) {
	return new Intl.DateTimeFormat(locale, {
		...options,
		numberingSystem: numberingSystem(locale),
	});
}

export function ContributionGraph({
	className,
	data = EMPTY_CONTRIBUTIONS,
	locale,
	labels,
	showLegend = true,
	showTooltips = true,
}: ContributionGraphProps) {
	const captionId = useId();
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);
	const shouldReduceMotion = useReducedMotion();

	const resolvedLabels = useMemo(
		() => ({ ...DEFAULT_LABELS, ...labels }),
		[labels],
	);

	const formatMonth = useMemo(
		() => createDateFormatter(locale, { month: 'short' }),
		[locale],
	);
	const formatWeekday = useMemo(
		() => createDateFormatter(locale, { weekday: 'short' }),
		[locale],
	);
	const formatFullDate = useMemo(
		() =>
			createDateFormatter(locale, {
				day: 'numeric',
				month: 'long',
				weekday: 'long',
				year: 'numeric',
			}),
		[locale],
	);
	const weekdays = useMemo(
		() =>
			Array.from({ length: DAYS_IN_WEEK }, (_, dayIndex) => ({
				isShown: SHOWN_WEEKDAYS.includes(dayIndex),
				label: formatWeekday.format(addDays(REFERENCE_SUNDAY, dayIndex)),
			})),
		[formatWeekday],
	);

	const weeks = useMemo(
		() => buildWeeks(data, (date) => formatMonth.format(date)),
		[data, formatMonth],
	);
	const monthHeaders = useMemo(() => buildMonthHeaders(weeks), [weeks]);

	const summary = useMemo(() => {
		const { start, end } = getWindowRange(data);
		return resolvedLabels.summary({
			days: data.length,
			total: data.reduce((total, day) => total + day.count, NO_CONTRIBUTIONS),
			from: formatFullDate.format(start),
			to: formatFullDate.format(end),
		});
	}, [data, formatFullDate, resolvedLabels]);

	const handleDayEnter = (
		day: ContributionData,
		cell: React.MouseEvent<HTMLDivElement>,
	) => {
		if (!showTooltips) return;
		const rect = cell.currentTarget.getBoundingClientRect();
		setTooltip({ day, x: rect.left + rect.width / 2, y: rect.top });
	};

	const handleDayLeave = () => {
		setTooltip(null);
	};

	const hasDays = weeks.length > 0;

	return (
		<MotionProvider>
			<figure
				aria-labelledby={captionId}
				className={cn(
					'contribution-graph corner-bevel glow-hover overflow-hidden rounded-xl border border-primary/20 bg-background-light',
					className,
				)}
			>
				<figcaption className="sr-only" id={captionId}>
					{summary}
				</figcaption>

				<div aria-hidden="true">
					<div className="flex items-center justify-between gap-3 border-primary/10 border-b px-4 py-3">
						<span className="font-mono text-2xs tracking-widest uppercase">
							<span className="text-primary">~/</span>
							<span className="text-muted-foreground">
								{resolvedLabels.kicker}
							</span>
						</span>
						<span className="truncate not-sm:hidden font-mono text-2xs tracking-widest text-muted-foreground/70 uppercase">
							{resolvedLabels.caption}
						</span>
					</div>

					{hasDays ? (
						<div className="overflow-x-auto p-4 scrollbar-thin">
							<div
								className="grid gap-1"
								style={{
									// label column + one flexible column per week.
									// 10px is the minimum dot size; below that the graph scrolls horizontally.
									gridTemplateColumns: `1.5rem repeat(${weeks.length}, minmax(10px, 1fr))`,
								}}
							>
								{monthHeaders.map((header) => (
									<div
										className="relative h-3 font-mono text-2xs tracking-widest text-muted-foreground uppercase"
										key={`${header.month}-${header.startWeek}`}
										style={{
											gridColumn: `${header.startWeek + 2} / span ${header.colspan}`,
											gridRow: 1,
										}}
									>
										<span className="absolute top-0 inset-s-0 whitespace-nowrap">
											{header.month}
										</span>
									</div>
								))}

								{weekdays.map((weekday, dayIndex) => (
									<div
										className="flex items-center font-mono text-2xs leading-none tracking-widest text-muted-foreground/70 uppercase"
										key={weekday.label}
										style={{ gridColumn: 1, gridRow: dayIndex + 2 }}
									>
										{weekday.isShown ? weekday.label : null}
									</div>
								))}

								{weeks.flatMap((week, weekIndex) =>
									week.days.map((day, dayIndex) => {
										const placement = {
											gridColumn: weekIndex + 2,
											gridRow: dayIndex + 2,
										};
										if (!day) {
											return (
												<div key={`${week.start}-${day}`} style={placement} />
											);
										}
										return (
											<div
												className={cn(
													'aspect-square w-full rounded-xs transition-[transform,box-shadow] duration-150 ease-out hover:z-10 hover:relative hover:scale-125 hover:shadow-(--shadow-glow) hover:ring-1 hover:ring-ring',
													getCellLevelClass(day.level),
												)}
												key={day.date}
												onMouseEnter={(event) => handleDayEnter(day, event)}
												onMouseLeave={handleDayLeave}
												style={placement}
											/>
										);
									}),
								)}
							</div>
						</div>
					) : (
						<p className="px-4 py-10 text-center font-mono text-2xs tracking-widest text-muted-foreground uppercase">
							{resolvedLabels.empty}
						</p>
					)}

					{showLegend && hasDays ? (
						<div className="flex items-center justify-between gap-3 border-primary/10 border-t px-4 py-3 font-mono text-2xs tracking-widest text-muted-foreground uppercase">
							<span>{resolvedLabels.legendLess}</span>
							<div className="flex items-center gap-1">
								{CELL_LEVEL_CLASSES.map((levelClass) => (
									<div
										className={cn('size-2.5 rounded-xs', levelClass)}
										key={levelClass}
									/>
								))}
							</div>
							<span>{resolvedLabels.legendMore}</span>
						</div>
					) : null}
				</div>

				<AnimatePresence>
					{showTooltips && tooltip ? (
						<m.div
							animate={{ opacity: 1, scale: 1 }}
							className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg border border-primary/40 bg-primary px-3 py-2 font-mono text-primary-foreground shadow-(--shadow-popover)"
							exit={
								shouldReduceMotion
									? { opacity: 0, transition: { duration: 0 } }
									: { opacity: 0, scale: 0.9 }
							}
							initial={
								shouldReduceMotion ? { opacity: 1 } : { opacity: 0, scale: 0.9 }
							}
							style={{
								left: tooltip.x,
								marginTop: -TOOLTIP_GAP_PX,
								top: tooltip.y,
							}}
							transition={
								shouldReduceMotion ? { duration: 0 } : { duration: 0.15 }
							}
						>
							<span className="block font-semibold text-2xs tracking-widest uppercase">
								{resolvedLabels.contribution(tooltip.day.count)}
							</span>
							<span className="block text-2xs opacity-80">
								{formatFullDate.format(parseIsoDate(tooltip.day.date))}
							</span>
						</m.div>
					) : null}
				</AnimatePresence>
			</figure>
		</MotionProvider>
	);
}

export default ContributionGraph;
